import { IconButton } from '@mui/material';
import { Box } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { LessonQuestionI } from 'types';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { colors } from 'Constants/Colors';
import { alpha } from '@mui/material';

interface Props {
  handlePreviousQuestion: () => void;
  handleNextQuestion: () => void;
  handleQuestionSelect: (index: number) => void;
  currentQuestionIndex: number;
  isFirstQuestion: boolean;
  isLastQuestion: boolean;
  questions: LessonQuestionI[];
  answers: Array<{ questionId: string; answer?: string }>;
}

const QuestionnairePagination = ({
  handlePreviousQuestion,
  handleNextQuestion,
  handleQuestionSelect,
  currentQuestionIndex,
  isFirstQuestion,
  isLastQuestion,
  questions,
  answers,
}: Props) => {
  const isQuestionAnswered = (index: number) => {
    const question = questions[index];
    const answer = answers.find(a => a.questionId === question.questionId);
    return answer?.answer && answer.answer.length > 0;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'center',
        alignItems: 'center',
        gap: 2,
        my: 3,
        px: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          width: '100%',
        }}
      >
        <IconButton
          onClick={handlePreviousQuestion}
          disabled={isFirstQuestion}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            color: colors.GREEN,
            flexShrink: 0,
            '&:hover': {
              backgroundColor: alpha(colors.GREEN, 0.1),
            },
            '&.Mui-disabled': {
              borderColor: 'grey.200',
              color: 'grey.400',
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
            maxHeight: '132px',
            overflowY: 'auto',
            overflowX: 'hidden',
            py: 1,
            px: 1,
            flex: 1,
            '&::-webkit-scrollbar': {
              width: '6px',
              backgroundColor: alpha(colors.GREEN, 0.1),
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors.GREEN,
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: alpha(colors.GREEN, 0.1),
              borderRadius: '3px',
            },
          }}
        >
          {questions.map((_, index) => {
            const isAnswered = isQuestionAnswered(index);
            const isActive = currentQuestionIndex === index;

            return (
              <IconButton
                key={index}
                onClick={() => handleQuestionSelect(index)}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: '2px solid',
                  flexShrink: 0,
                  borderColor: isActive ? colors.GREEN : isAnswered ? colors.GREEN : 'grey.300',
                  backgroundColor: isActive ? colors.GREEN : isAnswered ? alpha(colors.GREEN, 0.1) : 'transparent',
                  color: isActive ? 'white' : isAnswered ? colors.GREEN : colors.GREEN,
                  fontSize: '0.875rem',
                  '&:hover': {
                    backgroundColor: isActive ? colors.GREEN_SHINING : alpha(colors.GREEN, 0.1),
                  },
                }}
              >
                {index + 1}
              </IconButton>
            );
          })}
        </Box>

        <IconButton
          onClick={handleNextQuestion}
          disabled={isLastQuestion}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            color: colors.GREEN,
            flexShrink: 0,
            '&:hover': {
              backgroundColor: alpha(colors.GREEN, 0.1),
            },
            '&.Mui-disabled': {
              borderColor: 'grey.200',
              color: 'grey.400',
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default QuestionnairePagination;
